<svelte:options strictprops={false} />

<script>
  import { getContext } from "svelte";
  import upperFirst from "lodash/upperFirst";
  import { navigate } from "svelte-routing";

  import EditableHeader from "./sidebar/EditableHeader.svelte";
  import NavButton from "./toolbar/NavButton.svelte";

  import LaminaIcon from "@local/assets/icons/lamina.svg";
  import ShapeExchangeText from "@local/assets/icons/shape-exchange-text.svg";
  import ShpxIcon from "@local/assets/icons/shpx-ai-logo.svg";
  import CheckIcon from "@local/assets/icons/circle-check-filled.svg";
  // import HomeIcon from "@local/assets/icons/home-filled.svg";
  import GearIcon from "@local/assets/icons/gear.svg";
  import GroupIcon from "@local/assets/icons/group.svg";
  import ProductsIcon from "@local/assets/icons/products.svg";
  import VideoIcon from "@local/assets/icons/video.svg";
  import ExitIcon from "@local/assets/icons/exit.svg";
  import CaretDownIcon from "@local/assets/icons/caret-down.svg";

  import { clickOutside } from "svelte-utilities";
  import { user, profile } from "#src/stores/auth.js";
  import { navHistory, pathRoot } from "#src/stores/ui.js";
  import getUserInitials from "@local/extensions/formatters/get-user-initials.js";
  import { UX_MODE } from "#src/env.js";

  export let context = "home";
  export let group = null;
  /** @type {string | null} */
  export let tab = null;
  export let path;
  export let disabled = false;
  export let reviewerMode = false;
  export let docid;

  const colors = {
    "#6b7280": "#334155",
    "#ef4444": "#b91c1c",
    "#f97316": "#c2410c",
    "#f59e0b": "#b45309",
    "#eab308": "#a16207",
    "#84cc16": "#4d7c0f",
    "#22c55e": "#15803d",
    "#10b981": "#047857",
    "#14b8a6": "#0f766e",
    "#06b6d4": "#0e7490",
    "#0ea5e9": "#0369a1",
    "#3b82f6": "#1d4ed8",
    "#6366f1": "#4338ca",
    "#8b5cf6": "#6d28d9",
    "#a855f7": "#7e22ce",
    "#d946ef": "#a21caf",
    "#ec4899": "#be185d",
    "#f43f5e": "#be123c",
  };

  const org = getContext("org");

  let userDropdownButton;
  let showTabDropdown = false;
  let showUserDropdown = false;
  let tabDropdownButton;

  $: userInitials = getUserInitials($profile)?.toUpperCase() ?? "";
  $: home = context === "home" || context === "reviewer-job-list";
  $: capabilities = $profile?.profile_capabilities?.capabilities || {};
  $: canSeeProducts = capabilities.access_product_dashboard;
  $: canSeeOrgs = capabilities.switch_organization;

  async function logout() {
    showUserDropdown = false;
    navigate("/logout");
  }

  function toggleUserDropdown() {
    showUserDropdown = !showUserDropdown;
  }

  function hideUserDropdown() {
    showUserDropdown = false;
  }

  function navPath(pr, tab, buttontab, slug, j) {
    if (buttontab === tab) return null;
    const base = `${pr}${buttontab}`;
    if (slug && j) {
      const record = j[slug];
      if (record) {
        return `${base}/${slug}`;
      } else {
        return base;
      }
    }
    return base;
  }

  function updateJob(prop, value) {
    if (!group) return;
    group.updateJobProp(prop, value);
  }

  function clickTab(path) {
    showTabDropdown = !showTabDropdown;
    navigate(path);
  }
</script>

<nav class="navbar flex-none bg-black text-gray-400">
  <div class="px-4">
    <div class="flex items-center justify-between h-12">
      <div class="flex items-center gap-2 h-full">
        {#if UX_MODE === "shpx"}
          <button class="shpx-logo mr-4" on:click={() => navigate("/")}>
            <ShpxIcon />
          </button>
        {:else}
          <button class="logo-container" class:editor={!home} on:click={() => navigate("/")}>
            <div>
              <LaminaIcon />
            </div>
          </button>
          <button class="cursor-pointer pr-2" on:click={() => navigate("/")}>
            <ShapeExchangeText />
          </button>
        {/if}
        <div class="flex items-center">
          {#if context === "products"}
            <div class="text-white font-bold text-lg">Products</div>
          {:else if context === "reviewer-job-list"}
            <div class="text-white font-bold text-lg">Job Dashboard</div>
          {:else if context === "tally-request"}
            <div class="text-white font-bold text-lg">Tally Service</div>
          {:else if context === "group"}
            <div class="text-white font-bold text-lg">
              <EditableHeader
                inheritsize
                value={$group?.job.name}
                {disabled}
                maxwidth
                on:change={(e) => updateJob("name", e.detail.value)} />
            </div>
          {/if}
        </div>

        {#if $group}
          <div class="h-full text-sm hidden md:flex flex-none">
            {#if UX_MODE !== "shpx"}
              {#if reviewerMode && capabilities["show_ui:summary"]}
                <NavButton active={tab === "summary"} path={navPath($pathRoot, tab, "summary")}
                  >Summary</NavButton>
              {/if}
            {/if}
            {#if $group?.project_type !== "product" && capabilities["show_ui:documents"]}
              {#if UX_MODE === "shpx"}
                <NavButton
                  active={tab === "documents" && path?.split("/").length === 1}
                  path={`${$pathRoot}documents/`}>
                  Documents
                </NavButton>
                {#if docid}
                  {@const doc = $group?.documents[docid]}
                  {@const docName = doc?.name.length > 20 ? `${doc.name.slice(0, 20)}…` : doc?.name}
                  <NavButton
                    active={tab === "documents" && path?.split("/").length > 1}
                    path={`${$pathRoot}documents/${docid}`}
                    style="dynamic">
                    Current Workspace
                  </NavButton>
                {/if}
              {:else}
                <NavButton
                  active={tab === "documents"}
                  path={navPath($pathRoot, tab, "documents", $navHistory.documents, $group.documents)}>
                  Documents
                </NavButton>
              {/if}
            {/if}
            {#if UX_MODE !== "shpx"}
              {#if capabilities["show_ui:items"]}
                <NavButton
                  active={tab === "items"}
                  path={navPath($pathRoot, tab, "items", $navHistory.items, $group.items)}>
                  Items
                </NavButton>
              {/if}
              {#if $group?.project_type !== "product"}
                {#if capabilities["show_ui:openings"]}
                  <NavButton
                    active={tab === "openings"}
                    path={navPath($pathRoot, tab, "openings", $navHistory.collections, $group.items)}>
                    Openings
                  </NavButton>
                {/if}
                {#if capabilities["show_ui:types"]}
                  <NavButton
                    active={tab === "types"}
                    path={navPath($pathRoot, tab, "types", $navHistory.types, $group.types)}>
                    Types
                  </NavButton>
                {/if}
                {#if capabilities["show_ui:fabrications"]}
                  <NavButton
                    active={tab === "fabrications"}
                    path={navPath(
                      $pathRoot,
                      tab,
                      "fabrications",
                      $navHistory.fabrications,
                      $group.data.fabrications,
                    )}>
                    Fabrications
                  </NavButton>
                {/if}
              {/if}
            {/if}
          </div>

          <div
            class="md:hidden text-white text-xs relative flex-none"
            use:clickOutside={[tabDropdownButton]}
            on:outclick={() => (showTabDropdown = false)}>
            <button
              bind:this={tabDropdownButton}
              class="flex gap-1 items-center"
              on:click={() => (showTabDropdown = !showTabDropdown)}>
              <div>
                {upperFirst(tab)}
              </div>
              <span class="opacity-50">
                <CaretDownIcon />
              </span>
            </button>
            {#if showTabDropdown}
              <div class="dropdown-container left-0 top-0 mt-6">
                {#if reviewerMode && capabilities["show_ui:summary"]}
                  <button
                    class="dropdown-button-item"
                    on:click={() => clickTab(navPath($pathRoot, tab, "summary"))}>
                    Summary
                  </button>
                {/if}
                {#if $group?.project_type !== "product" && capabilities["show_ui:documents"]}
                  <button
                    class="dropdown-button-item"
                    on:click={() =>
                      clickTab(
                        navPath($pathRoot, tab, "documents", $navHistory.documents, $group.documents),
                      )}>
                    Documents
                  </button>
                {/if}
                {#if capabilities["show_ui:items"]}
                  <button
                    class="dropdown-button-item"
                    on:click={() =>
                      clickTab(navPath($pathRoot, tab, "items", $navHistory.items, $group.items))}>
                    Items
                  </button>
                {/if}
                {#if $group?.project_type !== "product"}
                  {#if capabilities["show_ui:openings"]}
                    <button
                      class="dropdown-button-item"
                      on:click={() =>
                        clickTab(navPath($pathRoot, tab, "openings", $navHistory.collections, $group.items))}>
                      Openings
                    </button>
                  {/if}
                  {#if capabilities["show_ui:types"]}
                    <button
                      class="dropdown-button-item"
                      on:click={() =>
                        clickTab(navPath($pathRoot, tab, "types", $navHistory.types, $group.types))}>
                      Types
                    </button>
                  {/if}
                  {#if capabilities["show_ui:fabrications"]}
                    <button
                      class="dropdown-button-item"
                      on:click={() =>
                        clickTab(
                          navPath(
                            $pathRoot,
                            tab,
                            "fabrications",
                            $navHistory.fabrications,
                            $group.data.fabrications,
                          ),
                        )}>
                      Fabrications
                    </button>
                  {/if}
                {/if}
              </div>
            {/if}
          </div>
        {:else if context === "products"}
          <div class="h-full text-sm hidden lg:flex">
            {#if $profile?.organization?.org_type === "supplier"}
              <NavButton active={tab === "makeups"} path="/products/makeups">Makeups</NavButton>
              <NavButton active={tab === "materials"} path="/products/materials">Materials</NavButton>
              <NavButton active={tab === "surfaces"} path="/products/surfaces">Surfaces</NavButton>
              <NavButton active={tab === "edges"} path="/products/edges">Edges</NavButton>
              <NavButton active={tab === "fabrications"} path="/products/fabrications">Fabs</NavButton>
              <NavButton active={tab === "surcharges"} path="/products/surcharges">Surcharges</NavButton>
              <!-- <NavButton active={tab === "settings"} path="/products/settings">Settings</NavButton> -->
              <NavButton active={tab === "items"} path="/products/items">Items</NavButton>
              <NavButton active={tab === "categories"} path="/products/categories">Categories</NavButton>
            {:else}
              <NavButton active={tab === "makeups"} path="/products/makeups">Makeups</NavButton>
              <NavButton active={tab === "materials"} path="/products/materials">Materials</NavButton>
              <NavButton active={tab === "surfaces"} path="/products/surfaces">Surfaces</NavButton>
              <NavButton active={tab === "edges"} path="/products/edges">Edges</NavButton>
              <NavButton active={tab === "fabrications"} path="/products/fabrications">Fabs</NavButton>
            {/if}
            <!-- <NavButton active={tab === "types"} path="/products/types">Types</NavButton> -->
          </div>

          <div
            class="lg:hidden text-white text-xs relative"
            use:clickOutside={[tabDropdownButton]}
            on:outclick={() => (showTabDropdown = false)}>
            <button
              bind:this={tabDropdownButton}
              class="flex gap-1 items-center"
              on:click={() => (showTabDropdown = !showTabDropdown)}>
              <div>
                {upperFirst(tab)}
              </div>
              <span class="opacity-50">
                <CaretDownIcon />
              </span>
            </button>
            {#if showTabDropdown}
              <div class="dropdown-container left-0 top-0 mt-6">
                {#if $profile?.organization?.org_type === "supplier"}
                  <button class="dropdown-button-item" on:click={() => clickTab("/products/makeups")}>
                    Makeups
                  </button>
                  <button class="dropdown-button-item" on:click={() => clickTab("/products/materials")}>
                    Materials
                  </button>
                  <button class="dropdown-button-item" on:click={() => clickTab("/products/surfaces")}>
                    Surfaces
                  </button>
                  <button class="dropdown-button-item" on:click={() => clickTab("/products/edges")}>
                    Edges
                  </button>
                  <button class="dropdown-button-item" on:click={() => clickTab("/products/fabrications")}>
                    Fabrications
                  </button>
                  <button class="dropdown-button-item" on:click={() => clickTab("/products/surcharges")}>
                    Surcharges
                  </button>
                  <!-- <button class="dropdown-button-item" on:click={() => clickTab("/products/settings")}>
                    Settings
                  </button> -->
                  <button class="dropdown-button-item" on:click={() => clickTab("/products/items")}>
                    Items
                  </button>
                  <button class="dropdown-button-item" on:click={() => clickTab("/products/categories")}>
                    Categories
                  </button>
                {:else}
                  <button class="dropdown-button-item" on:click={() => clickTab("/products/makeups")}>
                    Makeups
                  </button>
                  <button class="dropdown-button-item" on:click={() => clickTab("/products/materials")}>
                    Materials
                  </button>
                  <button class="dropdown-button-item" on:click={() => clickTab("/products/surfaces")}>
                    Surfaces
                  </button>
                  <button class="dropdown-button-item" on:click={() => clickTab("/products/edges")}>
                    Edges
                  </button>
                  <button class="dropdown-button-item" on:click={() => clickTab("/products/fabrications")}>
                    Fabrications
                  </button>
                {/if}
                <!-- <button class="dropdown-button-item" on:click={() => clickTab("/products/types")}>
                  Types
                </button> -->
              </div>
            {/if}
          </div>
        {/if}
      </div>

      <div>
        <div class="ml-4 flex gap-2 items-center md:ml-6">
          <div class="relative items-center">
            <button
              class="gap-4 items-center flex"
              bind:this={userDropdownButton}
              on:click|stopPropagation={toggleUserDropdown}>
              <div
                id="profile-organization-name"
                class="text-md font-bold hidden md:flex text-nowrap gap-2 items-center">
                {#if $org?.default_ux_mode === "pdf_exchange"}
                  <div class="bg-amber-500 rounded-full w-2 h-2"></div>
                {/if}
                <div>
                  {$profile?.organization?.name || ""}
                </div>
              </div>
              <button
                id="profile-circle-button"
                class="profile-circle-lg relative"
                style="
                  background-color: {colors[$profile?.user_color] || '#1d4ed8'};
                  border-color: {$profile?.user_color || '#3b82f6'};
                ">
                {userInitials}
              </button>
            </button>
            {#if showUserDropdown}
              <div
                class="absolute w-52 mt-2 right-0 z-40 bg-white border rounded text-gray-800 drop-shadow-lg text-sm"
                use:clickOutside={[userDropdownButton]}
                on:outclick={hideUserDropdown}>
                <div class="border-b py-2">
                  <div class="dropdown-item py-1 font-bold">
                    {($profile && $profile.username) || $user.email}
                  </div>
                  <div class="dropdown-item py-1 text-xs text-gray-500">
                    {$user.email}
                  </div>
                  <div class="dropdown-item py-1 text-xs text-gray-500 md:hidden">
                    {$profile?.organization?.name}
                  </div>
                </div>
                <div class="p-2 text-xs">
                  {#if $profile?.organization_id}
                    <button class="dropdown-button-item" on:click={() => navigate(`/settings`)}>
                      <GearIcon />
                      <div>Settings</div>
                    </button>
                  {/if}
                  {#if $profile?.organization?.org_type === "supplier" && canSeeProducts}
                    <button class="dropdown-button-item" on:click={() => navigate("/products")}>
                      <ProductsIcon />
                      <div>Products</div>
                    </button>
                  {/if}
                  <button class="dropdown-button-item" on:click={() => navigate("/help")}>
                    <VideoIcon />
                    <div>Help Videos</div>
                  </button>
                  {#if canSeeOrgs}
                    <button class="dropdown-button-item" on:click={() => navigate("/orgs")}>
                      <GroupIcon />
                      <div>Organizations</div>
                    </button>
                  {/if}
                  <button on:click={logout} class="dropdown-button-item">
                    <ExitIcon />
                    <div>Sign out</div>
                  </button>
                  {#if $org?.default_ux_mode === "pdf_exchange"}
                    <button class="dropdown-button-item" on:click={() => navigate("/pricing")}>
                      <div class="text-amber-500">
                        <CheckIcon />
                      </div>
                      <div>Upgrade</div>
                    </button>
                  {/if}
                </div>
              </div>
            {/if}
          </div>
        </div>
      </div>
    </div>
  </div>
</nav>

<style lang="scss">
  .logo-container {
    @apply flex-shrink-0 cursor-pointer w-6;
  }

  // shpx build only (the markup is UX_MODE-gated). Pins the height rather
  // than inheriting the SVG's intrinsic 26px, with vertical breathing room
  // inside the nav bar. NavbarBase sets the same class but never defines it —
  // Svelte scopes this rule to Navbar — so that logo renders at its intrinsic
  // size instead.
  .shpx-logo {
    @apply flex-shrink-0 cursor-pointer flex items-center;
    padding: 0.5rem 0;

    :global(svg) {
      height: 22px;
      width: auto;
    }
  }

  .navbar :global(a) {
    @apply text-white;
  }

  .navbar :global(.current) {
    @apply bg-gray-900;
  }
  .dropdown-item {
    @apply px-4 w-full text-left truncate;
  }

  .profile-circle-lg {
    @apply text-white h-8 w-8 text-center rounded-full text-sm border;
  }
</style>
